import React, {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { CspConfig, CspConfigPrivileges, getCspConfigId } from 'data/cspConfig/cspConfig.types';
import { AuditDetails, GridSettings, WithAuditDetails } from 'data/types';
import * as selectors from 'data/cspConfig/cspConfig.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/cspConfig/cspConfig.actions';
import * as userActions from 'users/users-actions';
import { AppToaster } from 'shared/toaster';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import CspConfigGrid from './components/CspConfigGrid';
import { cspConfigColumnDefs, cspConfigColumnDefsVersion } from '../../CspConfig.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  privileges: authenticationSelectors.getAuthenticationPrivileges(state),
  beforeLogout: authenticationSelectors.getAuthenticationBeforeLogout(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  data: selectors.getCspConfigList(state),
  selectedCspConfig: selectors.getSelectedCspConfig(state),
});

const mapDispatch = {
  getCspConfigs: actions.getCspConfigList,
  goToCspConfigCreate: actions.goToCspConfigCreate,
  goToCspConfigEdit: actions.goToCspConfigEdit,
  selectCspConfig: actions.selectCspConfig,
  deleteCspConfig: actions.deleteCspConfig,
  getGridSettings: userActions.getUserSettings,
  saveGridSettings: userActions.putUserSettings,
  goToCspConfigView: actions.goToCspConfigView,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

const USER_CATEGORY_SETTINGS = 'CspConfigSettings';

export const CspConfigList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  privileges,
  deliveryFunctionId,
  data = [],
  beforeLogout,
  selectedCspConfig,
  history,
  getCspConfigs,
  getGridSettings,
  saveGridSettings,
  goToCspConfigCreate,
  goToCspConfigEdit,
  selectCspConfig,
  deleteCspConfig,
  goToCspConfigView,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [cspConfigGridData, setCspConfigGridData] = useState<CspConfig[]>([]);

  useEffect(() => {
    getCspConfigs(deliveryFunctionId);

    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getCspConfigs,
  ]);

  useEffect(() => {
    setLastUpdatedAt(new Date());

    setCspConfigGridData(data);
  }, [data]);

  const handleRefresh = () => getCspConfigs(deliveryFunctionId);

  const handleCreateNew = () => goToCspConfigCreate(history);

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedRows: CspConfig[] = event.api.getSelectedRows();

    selectCspConfig(selectedRows);
  };

  const handleGetGridSettings = () => getGridSettings(USER_CATEGORY_SETTINGS, userName);

  const handleSaveGridSettings = (gridSettings: GridSettings) =>
    saveGridSettings(USER_CATEGORY_SETTINGS, userName, gridSettings);

  const handleCspConfigEdit = () => selectedCspConfig && goToCspConfigEdit(history);

  const handleCspConfigView = () => selectedCspConfig && goToCspConfigView(history);

  const handleCspConfigDelete = () => {
    const cspConfig = selectedCspConfig[0];

    if (cspConfig?.cspid == null) {
      return;
    }

    const cspConfigBody: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        cspConfig.name,
        AuditService.CSP_CONFIG,
        AuditActionType.CSP_CONFIG_DELETE,
      );
      cspConfigBody.auditDetails = auditDetails as AuditDetails;
    }

    deleteCspConfig(deliveryFunctionId, getCspConfigId(cspConfig), cspConfigBody);
  };

  const hasPrivilegesToCreate = privileges.includes(CspConfigPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(CspConfigPrivileges.Delete);
  const hasPrivilegeToView = privileges.includes(CspConfigPrivileges.View);

  return (
    <div data-testid="CspConfigList" style={{ height: 'inherit' }}>
      <CspConfigGrid
        selectedCspConfig={selectedCspConfig?.[0]}
        data={cspConfigGridData}
        columnDefs={cspConfigColumnDefs}
        columnDefsVersion={cspConfigColumnDefsVersion}
        noResults={false}
        beforeLogout={beforeLogout}
        lastUpdatedAt={lastUpdatedAt}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        hasPrivilegeToView={hasPrivilegeToView}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onGetGridSettings={handleGetGridSettings}
        onSaveGridSettings={handleSaveGridSettings}
        onSelectionChanged={handleSelectionChanged}
        onEdit={handleCspConfigEdit}
        onDelete={handleCspConfigDelete}
        onSelectItemMenuAction={handleCspConfigView}
      />
    </div>
  );
};

export default withRouter(connector(CspConfigList));
